import { createComponent } from '@/helpers/alpine';

type TValidationState = {
    hasNumber: boolean;
    hasUppercase: boolean;
    hasLowercase: boolean;
    hasSpecial: boolean;
    hasLength: boolean;
    hasMaxLength: boolean;
};

type TValidationTranslations = {
    hasNumber?: string;
    hasUppercase?: string;
    hasLowercase?: string;
    hasSpecial?: string;
    hasLength?: string;
    hasMaxLength?: string;
};

type TPasswordStrengthOpts = {
    translations?: TValidationTranslations;
    length?: number;
    maxLength?: number;
};

const VALIDATION_REGEX_MAP: { [key in keyof TValidationState]?: RegExp } = {
    hasNumber: /\d/,
    hasUppercase: /[A-Z]/,
    hasLowercase: /[a-z]/,
    hasSpecial: /[\p{P}\p{S}]/u,
};

const PasswordStrength = createComponent((opts: TPasswordStrengthOpts) => ({
    displayValidationList: false,
    translations: opts.translations ?? {},
    length: opts.length ?? 11,
    maxLength: opts.maxLength ?? 20,
    validationState: {
        hasNumber: false,
        hasUppercase: false,
        hasLowercase: false,
        hasSpecial: false,
        hasLength: false,
        hasMaxLength: false,
    } as TValidationState,
    show: false,

    init() {
        this.$refs.input.addEventListener('focus', () => this.setup(), {
            once: true,
        });
    },

    setup() {
        this.displayValidationList = true;
        this.$refs.input.addEventListener('keyup', this.validate.bind(this));
    },

    validate() {
        const input = this.$refs.input as HTMLInputElement;
        const value = input.value;

        const regexResults = Object.fromEntries(
            Object.entries(VALIDATION_REGEX_MAP).map(([key, regex]) => [
                key,
                regex!.test(value),
            ]),
        );

        const lengthResults = {
            hasLength: value.length >= this.length,
            hasMaxLength: value.length <= this.maxLength,
        };

        this.validationState = {
            ...this.validationState,
            ...regexResults,
            ...lengthResults,
        };

        const submit = input
            .closest('form')
            ?.querySelector('button[type="submit"]') as HTMLButtonElement;

        if (submit) {
            submit.disabled = !this.isValid;
        }
    },

    get isValid() {
        return Object.values(this.validationState).every(Boolean);
    },
}));

export { PasswordStrength };
